/**
 * Corrige texto UTF-8 que foi interpretado como Latin-1/Windows-1252
 * e gravado de novo (mojibake: "usuários" → "usuÃ¡rios").
 */

/** Map de code points Windows-1252 (0x80–0x9F) que não coincidem com Latin-1. */
const CP1252_TO_BYTE: Record<number, number> = {
  0x20ac: 0x80, // €
  0x201a: 0x82, // ‚
  0x0192: 0x83, // ƒ
  0x201e: 0x84, // „
  0x2026: 0x85, // …
  0x2020: 0x86, // †
  0x2021: 0x87, // ‡
  0x02c6: 0x88, // ˆ
  0x2030: 0x89, // ‰
  0x0160: 0x8a, // Š
  0x2039: 0x8b, // ‹
  0x0152: 0x8c, // Œ
  0x017d: 0x8e, // Ž
  0x2018: 0x91, // ‘
  0x2019: 0x92, // ’
  0x201c: 0x93, // “
  0x201d: 0x94, // ”
  0x2022: 0x95, // •
  0x2013: 0x96, // –
  0x2014: 0x97, // —
  0x02dc: 0x98, // ˜
  0x2122: 0x99, // ™
  0x0161: 0x9a, // š
  0x203a: 0x9b, // ›
  0x0153: 0x9c, // œ
  0x017e: 0x9e, // ž
  0x0178: 0x9f, // Ÿ
};

const MOJIBAKE_DETECT = /Ã|Â|â€|â”|â[\u201C\u201D\u2018\u2019\u20AC]/;

function charToLikelyByte(code: number): number | null {
  if (code <= 0xff) return code;
  return CP1252_TO_BYTE[code] ?? null;
}

function tryDecodeMojibake(text: string): string | null {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    const b = charToLikelyByte(text.charCodeAt(i));
    if (b == null) return null;
    bytes[i] = b;
  }
  try {
    const fixed = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    if (fixed.includes('\uFFFD')) return null;
    return fixed;
  } catch {
    return null;
  }
}

const FALLBACK_REPLACEMENTS: Array<[RegExp, string]> = [
  [/â€œ/g, '\u201C'],
  [/â€/g, '\u201D'],
  [/â€˜/g, '\u2018'],
  [/â€™/g, '\u2019'],
  [/â€”/g, '\u2014'],
  [/â€“/g, '\u2013'],
  [/â€¦/g, '\u2026'],
  [/Ã£/g, 'ã'],
  [/Ã¡/g, 'á'],
  [/Ã¢/g, 'â'],
  [/Ã /g, 'à'],
  [/Ã¤/g, 'ä'],
  [/Ã©/g, 'é'],
  [/Ãª/g, 'ê'],
  [/Ã¨/g, 'è'],
  [/Ã­/g, 'í'],
  [/Ã³/g, 'ó'],
  [/Ã´/g, 'ô'],
  [/Ãµ/g, 'õ'],
  [/Ãº/g, 'ú'],
  [/Ã¼/g, 'ü'],
  [/Ã§/g, 'ç'],
  [/Ã±/g, 'ñ'],
  [/Ã‰/g, 'É'],
  [/\u00C3\u2030/g, 'É'], // Ã + ‰ (CP1252 de É)
  [/\u00C3\u0161/g, 'Ú'], // Ã + š (CP1252 de Ú)
  [/Ãš/g, 'Ú'],
  [/Ã /g, 'à'],
  [/â”€/g, '─'],
  [/â”/g, '━'],
];

/**
 * Normaliza texto com mojibake. Seguro para texto já correcto (no-op).
 */
export function fixMojibake(text: string): string {
  if (!text || !MOJIBAKE_DETECT.test(text)) return text;

  // Pode haver várias camadas (UTF-8 → Latin-1 → UTF-8 → …).
  let current = text;
  for (let pass = 0; pass < 3; pass++) {
    if (!MOJIBAKE_DETECT.test(current)) break;
    const decoded = tryDecodeMojibake(current);
    if (decoded && decoded !== current && decoded.length <= current.length) {
      current = decoded;
      continue;
    }
    let replaced = current;
    for (const [pattern, value] of FALLBACK_REPLACEMENTS) {
      replaced = replaced.replace(pattern, value);
    }
    if (replaced === current) break;
    current = replaced;
  }
  return current;
}

/** Percorre objectos/arrays e corrige todas as strings (ex.: content JSON de aces/guias). */
export function fixMojibakeDeep<T>(value: T): T {
  if (typeof value === 'string') return fixMojibake(value) as T;
  if (Array.isArray(value)) return value.map((item) => fixMojibakeDeep(item)) as T;
  if (value && typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    // Só objectos plain — evita partir ícones Lucide, Dates, etc.
    if (proto !== Object.prototype && proto !== null) return value;
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      out[key] = fixMojibakeDeep(child);
    }
    return out as T;
  }
  return value;
}
